// Precondition: None
// Postcondition: Custom element that toggles dark/light theme and persists preference

import { icon } from '../../../shared/icons.js';

class ThemeToggle extends HTMLElement {
  private isDarkMode: boolean = false;
  private boundToggleHandler: () => void;

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.boundToggleHandler = () => this.toggleTheme();
  }

  connectedCallback(): void {
    this.initializeTheme();
    this.render();
    this.setupEventListeners();
    window.addEventListener('ft-theme-toggle', this.boundToggleHandler);
  }

  disconnectedCallback(): void {
    window.removeEventListener('ft-theme-toggle', this.boundToggleHandler);
  }

  private initializeTheme(): void {
    // Check localStorage for saved preference
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      this.isDarkMode = savedTheme === 'dark';
    } else {
      // Check system preference
      this.isDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    this.applyTheme();
  }

  private render(): void {
    const style = document.createElement('style');
    style.textContent = this.getStyles();

    const template = document.createElement('template');
    template.innerHTML = this.getTemplate();

    if (this.shadowRoot) {
      this.shadowRoot.appendChild(style);
      this.shadowRoot.appendChild(template.content.cloneNode(true));
    }
  }

  private getStyles(): string {
    return `
      :host {
        display: inline-block;
      }

      button {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 40px;
        height: 40px;
        padding: 0;
        background: var(--button-bg, transparent);
        border: 1px solid var(--button-border, var(--border, #E2E4E8));
        border-radius: var(--radius, 8px);
        cursor: pointer;
        transition: all 0.2s ease;
        color: var(--text, #1C1C1E);
      }

      button:hover {
        background: var(--button-hover-bg, #F4F4F5);
        border-color: var(--button-hover-border, #999999);
      }

      button:active {
        transform: scale(0.95);
      }

      button:focus {
        outline: var(--focus, 2px solid #1C1C1E);
        outline-offset: 2px;
      }

      svg {
        width: 20px;
        height: 20px;
        fill: currentColor;
      }

      @media (prefers-color-scheme: dark) {
        button {
          background: var(--button-bg, transparent);
          border-color: var(--button-border, #272a31);
        }

        button:hover {
          background: var(--button-hover-bg, #272a31);
          border-color: var(--button-hover-border, #555555);
        }
      }
    `;
  }

  private getTemplate(): string {
    return `
      <button aria-label="Toggle dark/light mode" title="Toggle theme">
        ${icon('sun-moon', 20)}
      </button>
    `;
  }

  private setupEventListeners(): void {
    const button = this.shadowRoot?.querySelector('button');
    if (button) {
      button.addEventListener('click', () => this.toggleTheme());
    }
  }

  private toggleTheme(): void {
    // Precondition: None
    // Postcondition: Theme is toggled, localStorage is updated, DOM is updated
    this.isDarkMode = !this.isDarkMode;
    this.applyTheme();
    localStorage.setItem('theme', this.isDarkMode ? 'dark' : 'light');
  }

  private applyTheme(): void {
    // Precondition: isDarkMode is set to desired state
    // Postcondition: HTML element has appropriate color-scheme and data-theme attribute
    const htmlElement = document.documentElement;
    if (this.isDarkMode) {
      htmlElement.style.colorScheme = 'dark';
      htmlElement.setAttribute('data-theme', 'dark');
    } else {
      htmlElement.style.colorScheme = 'light';
      htmlElement.setAttribute('data-theme', 'light');
    }
  }

  // Public API: Get current theme
  // Precondition: None
  // Postcondition: Returns 'dark' or 'light'
  public getTheme(): string {
    return this.isDarkMode ? 'dark' : 'light';
  }

  // Public API: Set theme programmatically
  // Precondition: theme is 'dark' or 'light'
  // Postcondition: Theme is applied and persisted
  public setTheme(theme: 'dark' | 'light'): void {
    this.isDarkMode = theme === 'dark';
    this.applyTheme();
    localStorage.setItem('theme', theme);
  }
}

// Register custom element (no export statement)

customElements.define('ft-theme-toggle', ThemeToggle);
